import { Link } from "react-router-dom";
import { ArrowRight, Heart, Compass, HandHeart, Sparkles } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { PageHeader, SectionLabel } from "../components/PageHeader";
import { Reveal } from "../lib/motionx";
import { useLang } from "../i18n";

const values = [
  { icon: Heart, title: "S — Sincerity", body: "Honest, transparent care at every step — from staffing and scheduling to quality of care. We believe sincere honesty is the foundation of trust." },
  { icon: Compass, title: "O — Ownership", body: "We lead with accountability and initiative in every detail — learning your family's dynamics, routines, and favorite things to build truly personalized care." },
  { icon: HandHeart, title: "A — Advocacy", body: "Every voice matters. We listen closely and stay active with managed care and pediatric organizations so families and staff alike are heard and supported." },
  { icon: Sparkles, title: "R — Resilience", body: "We meet challenges with hard work and tenacity, always finding ways to solve problems and give our clients the support they deserve to soar into a better tomorrow." },
];

export default function About() {
  const { t } = useLang();
  useSeo({
    title: "About Us | Amenity Health Services",
    description:
      "Learn about Amenity Health Services — our mission, values, and commitment to compassionate, respectful pediatric care for families in Houston, TX.",
  });

  return (
    <>
      <PageHeader
        label="About us"
        titleLines={["A caring team,", "here for your family"]}
        intro="Amenity Health Services exists to help children grow, communicate, and thrive — supported by a team that treats every family with warmth and respect."
      />

      {/* MISSION */}
      <section className="py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <SectionLabel className="mb-5">{t("Our mission")}</SectionLabel>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-ink leading-tight">
              {t("Helping every child feel supported, understood, and capable.")}
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.1}>
            <p className="text-lg text-ink/65 leading-relaxed">
              {t("Our mission is to provide compassionate, family-centered developmental care that helps children build the skills and confidence they need to flourish. We believe progress happens best in an environment of trust, patience, and genuine care — for the child and for the whole family.")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* VALUES — S.O.A.R. */}
      <section className="py-14 sm:py-20 bg-sage/25">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel className="mb-5">{t("Our values")}</SectionLabel>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-ink">{t("What makes us S.O.A.R.")}</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-black/5 bg-cream p-8">
                  <span className="grid place-items-center h-12 w-12 rounded-2xl bg-teal/10 text-teal mb-5">
                    <v.icon size={22} />
                  </span>
                  <h3 className="font-heading font-semibold text-xl text-ink">{t(v.title)}</h3>
                  <p className="mt-2 text-ink/60 leading-relaxed">{t(v.body)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENT */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-[2.5rem] bg-sage/25 px-8 py-14 sm:px-16 relative overflow-hidden">
              <HandHeart size={40} className="text-teal mb-5" />
              <h2 className="font-heading font-semibold text-2xl sm:text-4xl tracking-tight text-ink max-w-3xl leading-snug">
                {t("Our commitment: compassionate, respectful care for every child and family.")}
              </h2>
              <p className="mt-5 max-w-2xl text-ink/65 leading-relaxed text-lg">
                {t("We promise to listen, to explain things clearly, and to honor the trust you place in us. Every family deserves to feel welcomed, heard, and supported — and that's exactly what we aim to provide.")}
              </p>
              <Link
                to="/contact"
                data-testid="about-cta-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal px-7 py-4 font-medium text-white hover:bg-[#2E6088] transition-colors"
              >
                {t("Request Information")} <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
